import type { ImageProps } from 'antd'
import React, { useState, useEffect, useMemo } from 'react'
import { Form, Input, Row, Col, Table, Image, Space } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import { EasyImg } from 'src/components'
import { Title } from 'enasops/components'
import { mainStore } from 'enasops/stores'
import { $api } from 'src/plugins'
import { getImgBase } from 'src/utils/axios'
import { extendRules, getSingleRule, initFilter } from 'src/utils/common'

const ImageItem = (props: ImageProps) => {
  return <Image {...props} preview={{ mask: <EyeOutlined /> }} />
}

const ImageView = (props: { imgs: string[] }) => {
  const [visible, setVisible] = useState(false)

  return (
    <div className='op101__msn-imgview'>
      {
        props.imgs.length
          ? (
            <React.Fragment>
              <Image.PreviewGroup
                preview={{ visible: visible, onVisibleChange: setVisible }}
                items={props.imgs}
              >
                <ImageItem width={44} height={44} src={props.imgs[0]} />
              </Image.PreviewGroup>
              <EasyImg
                src={require('enasops/assets/op/101/search.png')}
                onClick={() => setVisible(true)}
                className='image-view'
              />
            </React.Fragment>
          )
          : <div className='text-left'>-</div>
      }
    </div>
  )
}

const MSNManage = (props: { id: string }) => {
  const [detail, setDetail] = useState<any>()
  useEffect(() => {
    const payload = initFilter({
      OrgId: mainStore.headers.OrgId,
      Include: ['Model1', 'Model2', 'Nav_Children', 'Nav_Children.Nav_Gateway']
    })
    extendRules(payload, [getSingleRule('ID', 1, props.id)])
    $api['op101/getMnSn'](payload).then(res => {
      res && setDetail(res)
    })
  }, [])

  const columns: any = useMemo(() => {
    const sharedOnCell = (record: any, index: number) => {
      return { rowSpan: index % 2 ? 0 : 2 }
    }
    return [
      { title: '名称', dataIndex: 'Name', onCell: sharedOnCell },
      { title: 'S/N码', dataIndex: 'SnNo', onCell: sharedOnCell },
      {
        title: '网口',
        dataIndex: ['Model', 'Network'],
        render: (text: any, record: any, index: number) => {
          return `网口${index % 2 + 1}（${text ?? '无'}）`
        }
      },
      { title: 'IP地址', dataIndex: ['Model', 'Address'], render: (text: string) => text || '-' },
      { title: '默认ECM', dataIndex: ['Model', 'Gateway4'], render: (text: string) => text || '-' },
      { title: '子网掩码', dataIndex: ['Model', 'Genmask'], render: (text: string) => text || '-' },
      { title: '首选DNS', dataIndex: ['Model', 'Dns'], render: (text: string) => text || '-' },
      { title: '备用DNS', dataIndex: ['Model', 'Dns1'], render: (text: string) => text || '-' },
      { title: '安装位置', dataIndex: 'Address', onCell: sharedOnCell, render: (text: string) => text || '-' },
      {
        title: '现场图片',
        dataIndex: 'IMG_FILE_PATH',
        onCell: sharedOnCell,
        render: (text: string) => {
          const imgs = JSON.parse(text || '[]').map((img: any) => `${mainStore.baseConfig.FileDomain || getImgBase()}${img.FilePath}`)
          return <ImageView imgs={imgs} />
        }
      },
    ]
  }, [])

  const getDataSource = () => {
    const initSource = detail?.Nav_Children ?? []
    const dataSource: any[] = []
    initSource.forEach((item: any) => {
      dataSource.push(
        { ...item, Model: item.Model1 },
        { ...item, Model: item.Model2 },
      )
    })
    return dataSource
  }

  const getLiveImgs = () => {
    const imgs = JSON.parse(detail?.IMG_FILE_PATH || '[]')
    return imgs.map((img: any) => `${mainStore.baseConfig.FileDomain || getImgBase()}${img.FilePath}`)
  }

  const liveImgs = getLiveImgs()
  return (
    <div className='op101__msn'>
      <Title title='Master信息' className='mg-t12 mg-b14' />
      <Form layout='vertical' className='op101__msn-master'>
        <Row gutter={16}>
          <Col span={4}>
            <Form.Item label='Master名称'>
              <Input disabled value={detail?.Name} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label='S/N码'>
              <Input disabled value={detail?.SnNo} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label='安装位置'>
              <Input disabled value={detail?.Address} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label='现场图片'>
              <Image.PreviewGroup>
                <Space>
                  {
                    liveImgs.map((img: string) => {
                      return <ImageItem key={img} width={32} height={32} src={img} />
                    })
                  }
                </Space>
              </Image.PreviewGroup>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Row gutter={16} className='mg-t12'>
        <Col span={12}>
          <div className='op101__msn-card'>
            <p className='card-title'>网口1（{detail?.Model1?.Network}）</p>
            <Form layout='horizontal'>
              <Row gutter={16}>
                <Col span={8}><Form.Item label='IP地址'>{detail?.Model1?.Address || '无'}</Form.Item></Col>
                <Col span={8}><Form.Item label='默认ECM'>{detail?.Model1?.Gateway4 || '无'}</Form.Item></Col>
                <Col span={8}><Form.Item label='子网掩码'>{detail?.Model1?.Genmask || '无'}</Form.Item></Col>
                <Col span={8}><Form.Item label='首选DNS'>{detail?.Model1?.Dns || '无'}</Form.Item></Col>
                <Col span={8}><Form.Item label='备用DNS'>{detail?.Model1?.Dns1 || '无'}</Form.Item></Col>
              </Row>
            </Form>
          </div>
        </Col>
        <Col span={12}>
          <div className='op101__msn-card'>
            <p className='card-title'>网口2（{detail?.Model2?.Network}）</p>
            <Form layout='horizontal'>
              <Row gutter={16}>
                <Col span={8}><Form.Item label='IP地址'>{detail?.Model2?.Address || '无'}</Form.Item></Col>
                <Col span={8}><Form.Item label='默认ECM'>{detail?.Model2?.Gateway4 || '无'}</Form.Item></Col>
                <Col span={8}><Form.Item label='子网掩码'>{detail?.Model2?.Genmask || '无'}</Form.Item></Col>
                <Col span={8}><Form.Item label='首选DNS'>{detail?.Model2?.Dns || '无'}</Form.Item></Col>
                <Col span={8}><Form.Item label='备用DNS'>{detail?.Model2?.Dns1 || '无'}</Form.Item></Col>
              </Row>
            </Form>
          </div>
        </Col>
      </Row>
      <Title title='Slave列表' className='mg-t20 mg-b18' />
      <Table
        bordered
        size='small'
        rowKey={(record, index) => `${record.ID}_${index}`}
        columns={columns}
        dataSource={getDataSource()}
        className='op101__msn-table'
      />
    </div>
  )
}

export default MSNManage